import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import normalizeAddonMenu from '../utils/normalizeAddonMenu';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { auth } from '../firebase';
import axios from 'axios';
import { getDeviceHeaders } from '../utils/deviceInfo';
import { traceLogout } from '../utils/logoutTrace';

const VALIDATE_USER_TIMEOUT_MS = 15000;
const VALIDATE_USER_DEDUPE_MS = 5000;
const BUSINESS_TOKEN_DEDUPE_MS = 5000;

let validateUserRequest = null;
let validateUserRequestStartedAt = 0;
let validateUserRequestKey = null;
const businessTokenRequests = new Map();

export function logoutToLogin(reason = 'no-business-access') {
  traceLogout(reason);
  auth.signOut();
  localStorage.removeItem('businessToken');
  localStorage.removeItem('businessesHooked');
  localStorage.removeItem('history');
  localStorage.removeItem('businessId');
  window.location.assign('/');
}

function getValidateUserRequest(userId, idToken, signal) {
  const now = Date.now();
  const canReuseRequest =
    validateUserRequest &&
    validateUserRequestKey === userId &&
    now - validateUserRequestStartedAt < VALIDATE_USER_DEDUPE_MS;

  if (canReuseRequest) {
    return validateUserRequest;
  }

  validateUserRequestStartedAt = now;
  validateUserRequestKey = userId;
  validateUserRequest = axios({
    method: 'POST',
    url: 'https://api.usenode.com/users/validate',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...getDeviceHeaders(userId),
    },
    signal,
    timeout: VALIDATE_USER_TIMEOUT_MS,
  });

  setTimeout(() => {
    if (validateUserRequestKey === userId) {
      validateUserRequest = null;
      validateUserRequestKey = null;
    }
  }, VALIDATE_USER_DEDUPE_MS);

  return validateUserRequest;
}

function getBusinessTokenRequest(userId, businessId, idToken, signal) {
  const requestKey = `${userId}:${businessId}`;
  const pendingRequest = businessTokenRequests.get(requestKey);
  if (pendingRequest) return pendingRequest;

  const request = axios({
    method: 'POST',
    url: 'https://api.usenode.com/users/business-token',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...getDeviceHeaders(userId),
    },
    data: { businessId },
    signal,
    timeout: VALIDATE_USER_TIMEOUT_MS,
  });

  businessTokenRequests.set(requestKey, request);
  request.then(
    () => {
      setTimeout(() => {
        if (businessTokenRequests.get(requestKey) === request) {
          businessTokenRequests.delete(requestKey);
        }
      }, BUSINESS_TOKEN_DEDUPE_MS);
    },
    () => {
      if (businessTokenRequests.get(requestKey) === request) {
        businessTokenRequests.delete(requestKey);
      }
    }
  );
  return request;
}

export const fetchBusinesses = createAsyncThunk(
  'core/fetchBusinesses',
  async (businessId, { dispatch, rejectWithValue, signal }) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return rejectWithValue('No user logged in');
      }
      const idToken = await user.getIdToken();
      const response = await getValidateUserRequest(user.uid, idToken, signal);

      const businesses = response?.data?.access;

      if (businessId && businesses) {
        const selectedBusiness = businesses.find(
          (business) => business?.businessId === businessId
        );
        if (selectedBusiness) {
          dispatch(setBusinessData(selectedBusiness));
        }
      }

      return businesses;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBusinessToken = createAsyncThunk(
  'core/fetchBusinessToken',
  async (businessId, { rejectWithValue, signal }) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        return rejectWithValue('No user logged in');
      }
      const idToken = await user.getIdToken();
      const response = await getBusinessTokenRequest(
        user.uid,
        businessId,
        idToken,
        signal
      );

      return response?.data?.access;
    } catch (error) {
      const status = error.response?.status;
      return rejectWithValue({
        status,
        data: error.response?.data || error.message,
      });
    }
  }
);

export const getBusinessData = createAsyncThunk(
  'core/getBusinessData',
  async ({ businessId, t, lang, brand }, { dispatch, getState }) => {
    const requestLang = (lang || getState().core.user?.lang)?.slice(0, 2) || 'fr';
    dispatch(setIsLoadingSection('MAIN'));
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: `main/general`,
      lang: requestLang,
      body: {
        lang: requestLang,
      },
    });
    dispatch(setBusinessData(response?.businessData));
    dispatch(setBusinessStructures(response?.structures));
    dispatch(setBusinessGroups(response?.groups));
    dispatch(setIsLoadingSection(null));
    dispatch(setBusinessInstallations(response?.installations));
    return response;
  }
);

export const getBosData = createAsyncThunk(
  'core/getBosData',
  async ({ businessId, t, lang, brand }, { dispatch, getState }) => {
    dispatch(setIsLoadingSection('BOS'));
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: `bosV4/general`,
      body: {
        lang: lang?.slice(0, 2) || 'fr',
      },
    });

    const state = getState();
    const isEditMode = state.core.isEditMode;
    const currentMenu = state.core.businessMenu;

    if (isEditMode && currentMenu && currentMenu.length > 0) {
      const updatedMenu = response?.menu?.map((serverItem) => {
        const currentItem = currentMenu.find(
          (item) => item.id === serverItem.id
        );
        if (currentItem) {
          return { ...serverItem, order: currentItem.order };
        }
        return serverItem;
      });

      dispatch(setBusinessMenu(updatedMenu));
    } else {
      dispatch(setBusinessMenu(response?.menu));
    }

    dispatch(setBusinessModules(response?.modules));
    dispatch(setBusinessExtensions(response?.extensions));
    dispatch(setBusinessLocations(response?.locations));
    dispatch(setBusinessCategories(response?.categories));
    dispatch(setBusinessEmails(response?.emails));
    dispatch(setBusinessPdfs(response?.pdfs || []));
    dispatch(setIsLoadingSection(null));
    return response;
  }
);

export const getMaestroData = createAsyncThunk(
  'core/getMaestroData',
  async ({ businessId, t, lang, brand }, { dispatch }) => {
    dispatch(setIsLoadingSection('MAESTRO'));
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: `maestro/general`,

      body: {
        lang: lang?.slice(0, 2) || 'fr',
      },
    });
    dispatch(setBusinessMaestro(response?.entity));
    dispatch(setIsLoadingSection(null));
    return response;
  }
);

export const getAuraData = createAsyncThunk(
  'core/getAuraData',
  async ({ businessId, t, lang, brand }, { dispatch }) => {
    dispatch(setIsLoadingSection('AURA'));
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: `aura/general`,
      body: {
        lang: lang?.slice(0, 2) || 'fr',
      },
    });
    dispatch(setBusinessAura(response?.mainBoard));
    dispatch(setBusinessBoards(response?.boards));
    dispatch(setIsLoadingSection(null));
    return response;
  }
);

export const getPulseData = createAsyncThunk(
  'core/getPulseData',
  async ({ businessId, t, lang, brand }, { dispatch }) => {
    dispatch(setIsLoadingSection('PULSE'));
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: `pulse/general`,
      body: {
        lang: lang?.slice(0, 2) || 'fr',
      },
    });
    dispatch(setBusinessPulses(response?.boards));
    dispatch(setIsLoadingSection(null));
    return response;
  }
);

export const getStructureOverview = createAsyncThunk(
  'core/getStructureOverview',
  async ({ structureId, t }) => {
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: 'main/structure-overview',
      body: { structureId },
    });
    return response;
  }
);

export const activateDemoMode = createAsyncThunk(
  'core/activateDemoMode',
  async ({ lang }, { dispatch, rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: 'bosV4/generate-demo-data',
        body: { lang: lang?.slice(0, 2) || 'fr', count: 20 },
      });

      if (response?.errorData) {
        console.error('Demo data fetch error:', response.errorData);
        return rejectWithValue(response.errorData);
      }

      const demoData = response?.demoData || {};
      if (Object.keys(demoData).length === 0) {
        console.error('Demo data empty, response:', response);
        return rejectWithValue('No demo data returned');
      }

      dispatch(setDemoData(demoData));
      dispatch(setDemoMode(true));
      return demoData;
    } catch (error) {
      console.error('activateDemoMode error:', error);
      return rejectWithValue(error?.message || 'Unknown error');
    }
  }
);

const getCachedBranding = () => {
  try {
    const raw = localStorage.getItem('businessBranding');
    if (!raw) return null;
    const cached = JSON.parse(raw);
    const activeId = localStorage.getItem('businessId');
    if (activeId && cached?.id && cached.id !== activeId) return null;
    return cached;
  } catch (e) {
    return null;
  }
};

function updateRecentBusinesses(business) {
  try {
    const businessId = business.id || business.businessId;
    if (!businessId || !business.mainColor) return;
    const raw = localStorage.getItem('recentBusinesses');
    let recent = raw ? JSON.parse(raw) : [];
    recent = recent.filter((b) => b.id !== businessId);
    recent.unshift({
      id: businessId,
      name: business.name || business.businessName || '',
      mainColor: business.mainColor || '#000000',
      secColor: business.secColor || '#69696990',
      font: business.font || 'Montserrat',
    });
    if (recent.length > 5) recent = recent.slice(0, 5);
    localStorage.setItem('recentBusinesses', JSON.stringify(recent));
  } catch (e) {}
}

const cachedBranding = getCachedBranding();

export const buildBusinessBrandingState = (business) => {
  if (!business) return null;
  const businessId = business.id || business.businessId;
  if (!businessId) return null;

  return {
    id: businessId,
    name: business.name || business.businessName || '',
    mainColor: business.mainColor || '#000000',
    secColor: business.secColor || '#69696990',
    accentColor: business.accentColor || '',
    idleColor: business.idleColor || '',
    successColor: business.successColor || '',
    errorColor: business.errorColor || '',
    font: business.font || 'Montserrat',
  };
};

const initialState = {
  businessData: {
    name: cachedBranding?.name || 'Node Canada',
    mainColor: cachedBranding?.mainColor || '#000000',
    secColor: cachedBranding?.secColor || '#69696990',
    font: cachedBranding?.font || 'Montserrat',
    ...(cachedBranding?.id && { id: cachedBranding.id }),
    ...(cachedBranding?.accentColor && { accentColor: cachedBranding.accentColor }),
    ...(cachedBranding?.idleColor && { idleColor: cachedBranding.idleColor }),
    ...(cachedBranding?.successColor && { successColor: cachedBranding.successColor }),
    ...(cachedBranding?.errorColor && { errorColor: cachedBranding.errorColor }),
  },
  businessMenu: [],
  businessModules: [],
  businessStructures: [],
  businessExtensions: [],
  businessAura: {},
  businessBoards: [],
  businessPulses: [],
  businessMaestro: {},
  businessLocations: [],
  businessCategories: [],
  businessEmails: [],
  businessPdfs: [],
  businessBilling: {},
  businessGroups: [],
  businessInstallations: [],
  activeModule: null,
  currentSection: '',
  user: {
    activeBusiness: null,
    isEmailConfirmed: false,
    isPhoneConfirmed: false,
    menuOpen: true,
    theme: 'light',
    lang: 'fr',
    displayScrollArea: false,
    fontSizeMultiplier: 1,
    posMode: {
      enabled:
        typeof window !== 'undefined' &&
        localStorage.getItem('posMode') === '1',
    },
    sandboxMode: {
      enabled:
        typeof window !== 'undefined' &&
        localStorage.getItem('sandboxMode') === '1',
    },
  },
  isAuthenticated: false,
  isLoadingSection: null,
  refreshCurrent: false,
  refreshCurrentIndex: null,
  refreshCurrentTab: null,
  businesses: [],
  businessesLoading: false,
  businessesError: null,
  loading: false,
  error: null,
  themeMode: 'light',
  isEditMode: false,
  isLayoutEditMode: false,
  isDemoMode: false,
  demoData: {},
  messages: [],
  eventMoveData: null,
  datePreview: null,
};

const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    setBusinessData: (state, action) => {
      const payload = action.payload;
      if (payload && payload.modules && !Array.isArray(payload.modules)) {
        payload.modules = Object.keys(payload.modules)
          .filter((k) => payload.modules[k])
          .map((k) => k.toUpperCase());
      }
      if (payload && !payload.id && payload.businessId) {
        payload.id = payload.businessId;
      }
      state.businessData = payload;

      const businessId = payload?.id || payload?.businessId;
      if (businessId) {
        try {
          localStorage.setItem('businessBranding', JSON.stringify({
            id: businessId,
            mainColor: payload.mainColor || '#000000',
            secColor: payload.secColor || '#69696990',
            accentColor: payload.accentColor || '',
            idleColor: payload.idleColor || '',
            successColor: payload.successColor || '',
            errorColor: payload.errorColor || '',
            font: payload.font || 'Montserrat',
            name: payload.name || payload.businessName || '',
          }));
        } catch (e) {}
        updateRecentBusinesses(payload);
      }
    },
    setBusinessInstallations: (state, action) => {
      state.businessInstallations = action.payload;
    },
    setBusinessMenu: (state, action) => {
      state.businessMenu = normalizeAddonMenu(action.payload);
    },
    setBusinessModules: (state, action) => {
      state.businessModules = action.payload;
    },
    setBusinessStructures: (state, action) => {
      state.businessStructures = action.payload;
    },
    setBusinessExtensions: (state, action) => {
      state.businessExtensions = action.payload;
    },
    setBusinessAura: (state, action) => {
      state.businessAura = action.payload;
    },
    setBusinessBoards: (state, action) => {
      state.businessBoards = action.payload;
    },
    setBusinessPulses: (state, action) => {
      state.businessPulses = action.payload;
    },
    setBusinessMaestro: (state, action) => {
      state.businessMaestro = action.payload;
    },
    setBusinessLocations: (state, action) => {
      state.businessLocations = action.payload;
    },
    setBusinessCategories: (state, action) => {
      state.businessCategories = action.payload;
    },
    setBusinessEmails: (state, action) => {
      state.businessEmails = action.payload;
    },
    setBusinessGroups: (state, action) => {
      state.businessGroups = action.payload;
    },
    setBusinessBilling: (state, action) => {
      state.businessBilling = action.payload;
    },
    setCurrentSectionState: (state, action) => {
      state.currentSection = action.payload;
    },
    setActiveModule: (state, action) => {
      state.activeModule = action.payload;
    },
    setRefreshCurrent: (state, action) => {
      state.refreshCurrent = action.payload;
    },
    setRefreshCurrentIndex: (state, action) => {
      state.refreshCurrentIndex = action.payload;
    },
    setRefreshCurrentTab: (state, action) => {
      state.refreshCurrentTab = action.payload;
    },
    setBusinessPdfs: (state, action) => {
      state.businessPdfs = action.payload;
    },
    setUser: (state, action) => {
      if (!action.payload) {
        state.user = null;
        state.isAuthenticated = false;
        return;
      }
      state.user = {
        ...state.user,
        ...action.payload,
        menuOpen:
          action.payload.menuOpen ?? state.user?.menuOpen ?? true,
      };
      state.isAuthenticated = true;
    },
    setUserMyWorkSummary: (state, action) => {
      if (!state.user) return;
      if (!state.user.activeBusiness) state.user.activeBusiness = {};
      state.user.activeBusiness.myWorkSummary = action.payload;
    },
    setUserEmailConfirmed: (state, action) => {
      state.user.isEmailConfirmed = action.payload;
    },
    setUserPhoneConfirmed: (state, action) => {
      state.user.isPhoneConfirmed = action.payload;
    },
    setUserFontSizeMultiplier: (state, action) => {
      if (!state.user) state.user = {};
      state.user.fontSizeMultiplier = action.payload;
    },
    setMenuOpen: (state, action) => {
      if (!state.user) state.user = {};
      state.user.menuOpen = action.payload;
    },
    setDisplayScrollArea: (state, action) => {
      if (!state.user) state.user = {};
      state.user.displayScrollArea = action.payload;
    },
    setThemeMode: (state, action) => {
      if (!state.user) state.user = {};
      state.user.theme = action.payload;
    },
    setActiveBusiness: (state, action) => {
      if (!state.user) state.user = {};
      state.user.activeBusiness = action.payload;
    },
    setLang: (state, action) => {
      if (!state.user) state.user = {};
      state.user.lang = action.payload;
    },
    setPosMode: (state, action) => {
      if (!state.user) state.user = {};
      const next = action.payload;
      const enabled =
        typeof next === 'boolean'
          ? next
          : Boolean(next?.enabled);
      state.user.posMode = { enabled };
      try { localStorage.setItem('posMode', enabled ? '1' : '0'); } catch (_) {}
    },
    setSandboxMode: (state, action) => {
      if (!state.user) state.user = {};
      const next = action.payload;
      const enabled =
        typeof next === 'boolean'
          ? next
          : Boolean(next?.enabled);
      state.user.sandboxMode = { enabled };
      try { localStorage.setItem('sandboxMode', enabled ? '1' : '0'); } catch (_) {}
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    toggleThemeMode: (state) => {
      state.themeMode = state.themeMode === 'light' ? 'dark' : 'light';
    },
    toggleEditMode: (state) => {
      const nextValue = !state.isEditMode;
      state.isEditMode = nextValue;
      if (nextValue) {
        state.isLayoutEditMode = false;
      }
    },
    setEditMode: (state, action) => {
      state.isEditMode = action.payload;
      if (action.payload) {
        state.isLayoutEditMode = false;
      }
    },
    toggleLayoutEditMode: (state) => {
      const nextValue = !state.isLayoutEditMode;
      state.isLayoutEditMode = nextValue;
      if (nextValue) {
        state.isEditMode = false;
      }
    },
    setLayoutEditMode: (state, action) => {
      state.isLayoutEditMode = action.payload;
      if (action.payload) {
        state.isEditMode = false;
      }
    },
    updateBusinessMenu: (state, action) => {
      state.businessMenu = normalizeAddonMenu(action.payload);
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
      if (state.messages.length > 20) {
        state.messages.shift();
      }
    },
    resetMessages: (state) => {
      state.messages = [];
    },
    setEventMoveData: (state, action) => {
      state.eventMoveData = action.payload;
    },
    clearEventMoveData: (state) => {
      state.eventMoveData = null;
    },
    setDatePreview: (state, action) => {
      state.datePreview = action.payload;
    },
    clearDatePreview: (state) => {
      state.datePreview = null;
    },
    setIsLoadingSection: (state, action) => {
      state.isLoadingSection = action.payload;
    },
    setDemoMode: (state, action) => {
      state.isDemoMode = action.payload;
      if (!action.payload) {
        state.demoData = {};
      }
    },
    setDemoData: (state, action) => {
      state.demoData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('RESET_STORE', () => {
        const cached = getCachedBranding();
        return {
          ...initialState,
          businessData: {
            name: cached?.name || '',
            mainColor: cached?.mainColor || '#000000',
            secColor: cached?.secColor || '#69696990',
            font: cached?.font || 'Montserrat',
            ...(cached?.id && { id: cached.id }),
          },
        };
      })
      .addCase(getBusinessData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessData.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getBusinessData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchBusinesses.pending, (state) => {
        state.businessesLoading = true;
        state.businessesError = null;
      })
      .addCase(fetchBusinesses.fulfilled, (state, action) => {
        state.businessesLoading = false;
        state.businesses = action.payload;
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.businessesLoading = false;
        state.businessesError = action.payload;
      });
  },
});

export const {
  setBusinessData,
  setUser,
  setUserMyWorkSummary,
  clearUser,
  setBusinessMenu,
  setBusinessModules,
  setBusinessStructures,
  setBusinessExtensions,
  setBusinessAura,
  setBusinessBoards,
  setBusinessPulses,
  setBusinessMaestro,
  setBusinessLocations,
  setBusinessCategories,
  setBusinessEmails,
  setCurrentSectionState,
  setActiveModule,
  toggleThemeMode,
  toggleEditMode,
  toggleLayoutEditMode,
  setEditMode,
  setLayoutEditMode,
  updateBusinessMenu,
  setBusinessPdfs,
  addMessage,
  setRefreshCurrent,
  setRefreshCurrentIndex,
  setRefreshCurrentTab,
  setUserEmailConfirmed,
  setUserPhoneConfirmed,
  setMenuOpen,
  resetMessages,
  setUserFontSizeMultiplier,
  setDisplayScrollArea,
  setThemeMode,
  setLang,
  setBusinessGroups,
  setBusinessInstallations,
  setEventMoveData,
  clearEventMoveData,
  setDatePreview,
  clearDatePreview,
  setIsLoadingSection,
  setActiveBusiness,
  setBusinessBilling,
  setPosMode,
  setSandboxMode,
  setDemoMode,
  setDemoData,
} = coreSlice.actions;
export default coreSlice.reducer;
